import { BlobModel } from '@/models/blob';
import { generateHexIdentifier } from '@/utils';
import { isImage, throwIfAborted, type Uploader } from './fileUploads';

/**
 * The form preview's uploader, and the tests': a picked file stays in memory in place of the upload.
 * It takes a local `preview-` identifier and counts as stored, so the field draws it as any other
 * stored file. Only an image gets a preview URL. One the browser cannot decode, such as a HEIC in
 * Chrome or Firefox, errors in the tile and falls back to the file-name tile, and nothing converts
 * it (docs/memory/entry-files.md). It has no metadata to read and no derivative is coming, so it
 * leaves `readMetadata` out.
 */
export const createMemoryUploader = (): Uploader => {
  /** Kept to download from and to revoke: on remove, and when the instance is dropped. */
  const objectUrls = new Map<string, string>();

  const release = (identifier: string) => {
    const url = objectUrls.get(identifier);
    if (url) {
      URL.revokeObjectURL(url);
      objectUrls.delete(identifier);
    }
  };

  return {
    async upload(_slot, file, onProgress, signal) {
      throwIfAborted(signal);
      const identifier = `preview-${generateHexIdentifier(12)}`;
      const url = URL.createObjectURL(file);
      objectUrls.set(identifier, url);
      // The module drops a file it aborts before its answer arrives, so it never releases that one.
      signal.addEventListener('abort', () => release(identifier), { once: true });
      const blob = new BlobModel({
        identifier,
        name: file.name,
        contentType: file.type,
        size: file.size,
        storedAt: new Date(),
        hasThumbnail: false
      });
      blob.file = file;
      blob.localPreviewUrl = isImage(file) ? url : undefined;
      onProgress(100);
      return blob;
    },

    /** Saves the file from its object URL, since the API has never seen it. */
    download(identifier, fileName) {
      const url = objectUrls.get(identifier);
      if (!url) {
        return;
      }
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    release
  };
};
